import ReactDatePicker from 'react-datepicker';
import { z } from 'zod';

import { ActivityUnit } from '@/app/org-cfv/src/enums/ActivityDataTypeEnum';
import yup from '@/utils/yup';

export const DefaultBoundaryLimit = 100;

export enum DocumentType {
  GENERAL = 'GENERAL',
  FACTOR = 'FACTOR',
  NAME_PLATE = 'NAME_PLATE',
  REFRIGERANT = 'REFRIGERANT',
  SPECIFICATIONS = 'SPECIFICATIONS',
  PCR = 'PCR',
  WEEE = 'WEEE',
  TD_LOSS = 'TD_LOSS',
  UNIT_CONVERSION = 'UNIT_CONVERSION',
  USER = 'USER',
  AI = 'AI',
}

export const documentSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(DocumentType),
});

export const documentSchemaAlt = z.object({
  fileId: z.string().uuid(),
  type: z.nativeEnum(DocumentType),
});

export enum Order {
  ASC = 'ASC',
  DESC = 'DESC',
}

export enum MatchType {
  AND = 'ALL',
  OR = 'ANY',
}

export interface Meta {
  page: number;
  take: number;
  itemCount: number;
  pageCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface SubmittedBy {
  userId: string;
  name: string;
  departmentName: string;
  departmentId: string;
}

export interface ResponseBody<T = void> {
  status: string;
  code: number;
  data: T;
}

export enum ResponseStatus {
  OK = 'ok',
  ERROR = 'error',
}

export interface Document {
  id: string;
  name: string;
  downloadLink: string;
}

export interface FileDropZoneDocument {
  fileId: string;
  type: DocumentType;
}

export type DatePickerExtend = ReactDatePicker & { input: HTMLInputElement };

export type ProviderActionMap<M extends { [index: string]: unknown }> = {
  [Key in keyof M]: M[Key] extends undefined
    ? {
        type: Key;
      }
    : {
        type: Key;
        payload: M[Key];
      };
};

export enum FileType {
  PDF = '.pdf',
  JPEG = '.jpeg',
  JPG = '.jpg',
  PNG = '.png',
  XLS = '.xls',
  XLSX = '.xlsx',
  CSV = '.csv',
}

export type DeleteModalTypes = 'deleting' | 'deleted' | 'deleteFailed';
export type DeactivateModalTypes = 'activated' | 'deactivating' | 'deactivated';
export const FACTOR_MAX_DECIMAL_PLACES = 10;
export enum ScenarioConfigurationType {
  USAGE = 'usage',
  END_OF_LIFE = 'endOfLife',
}

export enum LocalStorageKey {
  BOUNDARY = 'boundary',
  SPACE = 'space',
  BOUNDARY_PROJECT = 'boundaryProject',
  PROJECT_SYNC = 'project-sync-states',
}
export enum SessionStorageKey {
  TAB_ID = 'tab-id',
}
export interface LocalBoundary {
  id: string;
}
export interface LocalBoundaryProject {
  id: string;
}
export interface LocalSpace {
  id: string;
}
export interface BulkInputValidateResult {
  code?: number;
  msg?: string;
}

export interface ValueUnit<T = number, R = ActivityUnit> {
  value: T;
  unit: R;
}

export interface FactorUnit<T = number, R = ActivityUnit> {
  value: T;
  ghgUnit: R;
  activityUnit: R | string;
}

export enum ReviewAction {
  NONE = '',
  APPROVE = 'approve',
  REJECT = 'reject',
}

export enum ReviewStatus {
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

// Using Submitting and Removing as generic names for cases like
// Approving or Reject, Resending or Deleting etc
// this is useful for pages with table row and bulk functions
export interface ButtonLoadingState {
  isBulkSubmitting: boolean;
  isBulkRemoving: boolean;
  isSubmitting: boolean;
  isRemoving: boolean;
  id?: string | null;
}

export enum AI_MAPPING_CATEGORY {
  RAW_MATERIAL = 'raw_material',
  VENDOR = 'vendor',
  CUSTOMER = 'customer',
}

export const ySchema = (() => {
  const DocumentSchemaAlt = yup.object().shape({
    fileId: yup.string().uuid(),
    type: yup.mixed<DocumentType>().oneOf(Object.values(DocumentType)),
  });

  return {
    DocumentSchemaAlt,
  };
})();
